import { useState } from "react";
import { Sparkles, X, Loader2, Check, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { errorMessage } from "@/lib/errors";

/**
 * BriefHelperModal — ARIA pops up when a client posts a vague job and
 * walks them through 3-4 clarifying questions, then rewrites the brief.
 *
 * Props:
 *   • brief: the in-progress job description
 *   • category: current category selection (helps ARIA pick the right questions)
 *   • skills: current skill tags
 *   • onAccept(newBrief): apply the rewritten brief to the parent form
 *   • onClose(): dismiss without changes
 *
 * Two-phase flow:
 *   Phase 1: POST /aria/brief-helper → returns 3-4 questions
 *   Phase 2: POST /aria/brief-rewrite with answers → returns rewritten brief
 *
 * The whole flow is stateless on the backend — answers live in this
 * component's state until the user clicks "Use this brief".
 */
export default function BriefHelperModal({ brief, category, skills, onAccept, onClose }) {
  const [phase, setPhase] = useState("intro"); // intro | questions | rewriting | preview
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [rewritten, setRewritten] = useState("");
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);

  const start = async () => {
    setBusy(true);
    try {
      const res = await api.post("/marketplace/aria/brief-helper", {
        brief, category, skills,
      });
      if (!res.data?.needs_help || !res.data?.questions?.length) {
        toast.success("Brief looks great — you're good to post");
        onClose();
        return;
      }
      setQuestions(res.data.questions);
      setPhase("questions");
    } catch (err) {
      toast.error(errorMessage(err, "ARIA is offline — post as-is"));
      onClose();
    } finally { setBusy(false); }
  };

  const finalize = async () => {
    setBusy(true);
    setPhase("rewriting");
    try {
      const answersList = questions
        .map((q) => ({ question: q, answer: (answers[q] || "").trim() }))
        .filter((a) => a.answer);
      if (answersList.length === 0) {
        toast.error("Answer at least one question so ARIA has something to work with");
        setPhase("questions");
        return;
      }
      const res = await api.post("/marketplace/aria/brief-rewrite", {
        brief, category, answers: answersList,
      });
      setRewritten(res.data?.rewritten_brief || brief);
      setPhase("preview");
    } catch (err) {
      toast.error(errorMessage(err, "Couldn't rewrite — keeping your original brief"));
      onClose();
    } finally { setBusy(false); }
  };

  const useBrief = () => {
    onAccept(rewritten);
    toast.success("Brief updated — ready to post");
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[2100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 overflow-y-auto"
      onClick={() => !busy && onClose()}>
      <div onClick={(e) => e.stopPropagation()}
        data-testid="brief-helper-modal"
        className="w-full max-w-xl bg-luxury-black border border-gold/40 rounded-lg p-6 space-y-4 my-8 shadow-2xl shadow-gold/10">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-full bg-gold/15 flex items-center justify-center">
              <Sparkles size={16} className="text-gold" />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] text-gold">ARIA</p>
              <h3 className="font-heading text-lg text-white">Brief Writer</h3>
            </div>
          </div>
          <button onClick={onClose} className="text-white/50 hover:text-white"><X size={20} /></button>
        </div>

        {phase === "intro" && (
          <div className="space-y-3">
            <p className="text-sm text-white/75">
              Your brief looks a bit short. Want me to ask 3 quick questions
              and rewrite it so freelancers can quote accurately? Takes 30 seconds
              and gets 2× better bids.
            </p>
            <div className="p-3 bg-black/40 border border-white/10 rounded text-xs text-white/55 italic">
              "{brief}"
            </div>
            <div className="flex gap-2">
              <button data-testid="brief-helper-start" onClick={start} disabled={busy}
                className="flex-1 inline-flex items-center justify-center gap-2 py-2.5 bg-gold text-luxury-black text-xs font-body uppercase tracking-wider rounded hover:bg-gold/90 disabled:opacity-40">
                {busy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} Help me improve it
              </button>
              <button data-testid="brief-helper-skip" onClick={onClose} disabled={busy}
                className="px-4 py-2.5 border border-white/15 text-white/60 hover:text-white text-xs uppercase tracking-wider rounded">
                Post as-is
              </button>
            </div>
          </div>
        )}

        {phase === "questions" && (
          <div className="space-y-3">
            <p className="text-[11px] text-white/50 uppercase tracking-wider">3 quick clarifying questions</p>
            {questions.map((q, i) => (
              <div key={i} className="space-y-1">
                <p className="text-xs text-gold/90 font-body">{i + 1}. {q}</p>
                <textarea
                  data-testid={`brief-answer-${i}`}
                  value={answers[q] || ""}
                  onChange={(e) => setAnswers((cur) => ({ ...cur, [q]: e.target.value }))}
                  rows={2}
                  maxLength={500}
                  placeholder="Your answer…"
                  className="w-full bg-black/40 border border-white/15 rounded px-3 py-2 text-sm text-white placeholder-white/30 focus:border-gold/60 focus:outline-none"
                />
              </div>
            ))}
            <button data-testid="brief-helper-finalize" onClick={finalize} disabled={busy}
              className="w-full inline-flex items-center justify-center gap-2 py-2.5 bg-gold text-luxury-black text-xs font-body uppercase tracking-wider rounded hover:bg-gold/90 disabled:opacity-40">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <ArrowRight size={14} />}
              Rewrite my brief
            </button>
          </div>
        )}

        {phase === "rewriting" && (
          <div className="flex flex-col items-center gap-2 py-8">
            <Loader2 size={24} className="animate-spin text-gold" />
            <p className="text-xs text-white/50">ARIA is rewriting your brief…</p>
          </div>
        )}

        {phase === "preview" && (
          <div className="space-y-3">
            <p className="text-[11px] text-white/50 uppercase tracking-wider">ARIA's rewrite</p>
            {editing ? (
              <textarea
                data-testid="brief-helper-edit"
                value={rewritten}
                onChange={(e) => setRewritten(e.target.value)}
                rows={10}
                className="w-full bg-black/40 border border-gold/30 rounded px-3 py-2 text-sm text-white focus:border-gold/60 focus:outline-none"
              />
            ) : (
              <div data-testid="brief-helper-preview" className="p-3 bg-gradient-to-br from-gold/[0.08] to-transparent border border-gold/30 rounded text-sm text-white whitespace-pre-line">
                {rewritten}
              </div>
            )}
            <div className="flex flex-wrap gap-2">
              <button data-testid="brief-helper-accept" onClick={useBrief}
                className="flex-1 inline-flex items-center justify-center gap-2 py-2.5 bg-gold text-luxury-black text-xs font-body uppercase tracking-wider rounded hover:bg-gold/90">
                <Check size={14} /> Use this brief
              </button>
              <button data-testid="brief-helper-edit-toggle" onClick={() => setEditing(!editing)}
                className="px-4 py-2.5 border border-white/15 text-white/70 hover:text-white text-xs uppercase tracking-wider rounded">
                {editing ? "Done editing" : "Edit"}
              </button>
              <button data-testid="brief-helper-discard" onClick={onClose}
                className="px-4 py-2.5 border border-white/15 text-white/60 hover:text-white text-xs uppercase tracking-wider rounded">
                Keep original
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
